/**
 * THE canonical definition of the customer fields.
 *
 * The same field list was previously maintained by hand in three places -- saveFormState, the
 * submit payload, and the ShareConfigButton props object -- so adding a field meant editing
 * three lists and forgetting one was silent. Everything now derives from this map.
 *
 * Each entry declares:
 *   label        visible field label
 *   required     drives the "*" marker and the submit blocker list
 *   type         input type
 *   autoComplete browser autofill token
 *   inputMode    mobile keyboard hint
 *   validate     key into lib/validation/customer.js
 *   persist      'always' | 'onSubmit' -- see the PII note below
 *   share        include in a shareable config URL
 *   payload      include in the checkout request body
 *   mask         optional input formatter
 *
 * PII AND PERSISTENCE: `persist: 'onSubmit'` marks fields that must NOT be written to
 * localStorage on every keystroke. The blob is readable by any XSS on the origin and lingers
 * on shared or kiosk browsers. Encryption would be theatre (the key would sit in the same
 * bundle); scope reduction is the real fix -- write customer PII only when the user actually
 * leaves for Stripe, which is the only reason it is persisted at all.
 */

export const CUSTOMER_FIELDS = {
  firstName: {
    label: 'First Name',
    required: true,
    type: 'text',
    autoComplete: 'given-name',
    validate: 'firstName',
    persist: 'onSubmit',
    share: true,
    payload: true,
  },
  lastName: {
    label: 'Last Name',
    required: true,
    type: 'text',
    autoComplete: 'family-name',
    validate: 'lastName',
    persist: 'onSubmit',
    share: true,
    payload: true,
  },
  email: {
    label: 'Email Address',
    required: true,
    type: 'email',
    autoComplete: 'email',
    inputMode: 'email',
    validate: 'email',
    persist: 'onSubmit',
    share: true,
    payload: true,
  },
  phone: {
    label: 'Phone Number',
    required: true,
    type: 'tel',
    autoComplete: 'tel',
    inputMode: 'tel',
    validate: 'phone',
    persist: 'onSubmit',
    share: true,
    payload: true,
    mask: 'us-phone',
  },
  customerWebsite: {
    label: 'Website URL',
    required: false,
    type: 'text',
    autoComplete: 'url',
    inputMode: 'url',
    validate: 'customerWebsite',
    persist: 'onSubmit',
    share: true,
    payload: true,
    placeholder: 'example.com',
  },
  companyName: {
    label: 'Company Name',
    required: false,
    type: 'text',
    autoComplete: 'organization',
    validate: 'companyName',
    persist: 'onSubmit',
    share: true,
    payload: true,
  },
};

/** Field names in display order. */
export const CUSTOMER_FIELD_NAMES = Object.keys(CUSTOMER_FIELDS);

/** Required field names, in the order the submit blocker list should walk them. */
export const REQUIRED_CUSTOMER_FIELDS = CUSTOMER_FIELD_NAMES.filter(
  (name) => CUSTOMER_FIELDS[name].required
);

/** Fields that carry PII. Used by the persistence layer and to keep them out of logs. */
export const PII_FIELDS = CUSTOMER_FIELD_NAMES.filter(
  (name) => CUSTOMER_FIELDS[name].persist === 'onSubmit'
);

/** An empty customer object with every field present. */
export function emptyCustomer() {
  return CUSTOMER_FIELD_NAMES.reduce((acc, name) => ({ ...acc, [name]: '' }), {});
}

/** Pick only the fields that belong in the checkout request body. */
export function toPayloadFields(customer = {}) {
  return CUSTOMER_FIELD_NAMES.filter((name) => CUSTOMER_FIELDS[name].payload).reduce(
    (acc, name) => ({ ...acc, [name]: customer[name] ?? '' }),
    {}
  );
}

/** Pick only the fields that belong in a shareable config URL. */
export function toShareFields(customer = {}) {
  return CUSTOMER_FIELD_NAMES.filter((name) => CUSTOMER_FIELDS[name].share).reduce((acc, name) => {
    const value = customer[name];
    if (value) acc[name] = value;
    return acc;
  }, {});
}
